import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ColorProvider } from './contexts/ColorContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { ScanProvider } from './contexts/ScanContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import WebSocketProvider from './components/Common/WebSocketProvider';
import { AlertNotificationProvider } from './contexts/AlertNotificationContext';
import './i18n'; // Initialize i18n
import './App.css';
import './styles/theme-overrides.css'; /* Consolidated dark/light mode - MUST be last */
import LoadingSpinner from './components/Common/LoadingSpinner';

// Eager imports — always needed on initial load
import Layout from './components/Layout/Layout';
import Dashboard from './components/Dashboard/Dashboard';
import Projects from './components/Projects/Projects';
import Scans from './components/Scans/Scans';
import Login from './components/Auth/Login';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Lazy imports — loaded on demand for code splitting
const ProjectDetail = React.lazy(() => import('./components/Projects/ProjectDetail'));
const ProjectOverview = React.lazy(() => import('./components/Projects/ProjectOverview'));
const ProjectTargets = React.lazy(() => import('./components/Projects/ProjectTargets'));
const ProjectScans = React.lazy(() => import('./components/Projects/ProjectScans'));
const ProjectSchedules = React.lazy(() => import('./components/Projects/ProjectSchedules'));
const ProjectResults = React.lazy(() => import('./components/Projects/ProjectResults'));
const ProjectReports = React.lazy(() => import('./components/Projects/ProjectReports'));
const ProjectAgents = React.lazy(() => import('./components/Projects/ProjectAgents'));
const ProjectExposure = React.lazy(() => import('./components/Projects/ProjectExposure'));
const BreachMonitoring = React.lazy(() => import('./components/Projects/BreachMonitoring'));
const ScanDetail = React.lazy(() => import('./components/Scans/ScanDetail'));
const ScanCompare = React.lazy(() => import('./components/Scans/ScanCompare'));
const ScanStatistics = React.lazy(() => import('./components/Scans/ScanStatistics'));
const ScanForm = React.lazy(() => import('./components/Scans/ScanForm'));
const Results = React.lazy(() => import('./components/Results/Results'));
const VulnData = React.lazy(() => import('./components/VulnData/VulnData'));
const VulnDataRules = React.lazy(() => import('./components/VulnData/VulnDataRules'));
const VulnDataDatabases = React.lazy(() => import('./components/VulnData/VulnDataDatabases'));
const VulnDataImports = React.lazy(() => import('./components/VulnData/VulnDataImports'));
const VulnDataConfig = React.lazy(() => import('./components/VulnData/VulnDataConfig'));
const CVEImport = React.lazy(() => import('./components/VulnData/CVEImport'));
const ImportPresets = React.lazy(() => import('./components/VulnData/ImportPresets'));
const RetireJS = React.lazy(() => import('./components/RetireJS/RetireJS'));
const NucleiTemplateManager = React.lazy(() => import('./components/VulnData/NucleiTemplateManager'));
const KEVDashboard = React.lazy(() => import('./components/Security/KEVDashboard'));
const DiscoveryPatterns = React.lazy(() => import('./components/Admin/DiscoveryPatterns'));
const SystemHealth = React.lazy(() => import('./components/Admin/SystemHealth'));
const ReconPolicies = React.lazy(() => import('./components/Admin/ReconPolicies'));
const VulnContextEditor = React.lazy(() => import('./components/Admin/VulnContextEditor'));
const EngineLimits = React.lazy(() => import('./components/Admin/EngineLimits'));
const SecurityDashboard = React.lazy(() => import('./components/SecurityDashboard/SecurityDashboard'));
const AlertsPage = React.lazy(() => import('./components/SecurityDashboard/AlertsPage'));
const VulnerabilitiesPage = React.lazy(() => import('./components/SecurityDashboard/VulnerabilitiesPage'));
const ThreatIntelPage = React.lazy(() => import('./components/SecurityDashboard/ThreatIntelPage'));
const FeedsPage = React.lazy(() => import('./components/SecurityDashboard/FeedsPage'));
const OSINTIntelligence = React.lazy(() => import('./components/SecurityDashboard/OSINTIntelligence'));
const WordPressDashboard = React.lazy(() => import('./components/WordPress/WordPressDashboard'));
const DiscoveryScan = React.lazy(() => import('./components/Discovery/DiscoveryScan'));
const ClientReviews = React.lazy(() => import('./components/ClientReviews/ClientReviews'));
const ClientReviewDetail = React.lazy(() => import('./components/ClientReviews/ClientReviewDetail'));
const Admin = React.lazy(() => import('./components/Admin/Admin'));
const FindingManagement = React.lazy(() => import('./components/Findings/FindingManagement'));
const ExposureMonitoring = React.lazy(() => import('./components/Findings/ExposureMonitoring'));
const Settings = React.lazy(() => import('./components/Settings/Settings'));
const ForgotPassword = React.lazy(() => import('./components/Auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./components/Auth/ResetPassword'));

// Lazy imports from barrel files — need named re-exports as default
const ReconScan = React.lazy(() => import('./components/Recon').then(m => ({ default: m.ReconScan })));
const ReconResults = React.lazy(() => import('./components/Recon').then(m => ({ default: m.ReconResults })));
const AttackSurfaceDashboard = React.lazy(() => import('./components/Recon').then(m => ({ default: m.AttackSurfaceDashboard })));
const CorrelatedFindings = React.lazy(() => import('./components/Recon').then(m => ({ default: m.CorrelatedFindings })));
const Agents = React.lazy(() => import('./components/Agents').then(m => ({ default: m.Agents })));
const Schedules = React.lazy(() => import('./components/Schedules').then(m => ({ default: m.Schedules })));
const ScheduleDetail = React.lazy(() => import('./components/Schedules').then(m => ({ default: m.ScheduleDetail })));
const Clients = React.lazy(() => import('./components/Clients').then(m => ({ default: m.Clients })));
const ClientDetail = React.lazy(() => import('./components/Clients').then(m => ({ default: m.ClientDetail })));
const ClientAccount = React.lazy(() => import('./components/Clients').then(m => ({ default: m.ClientAccount })));
const ScanReports = React.lazy(() => import('./components/Reports').then(m => ({ default: m.ScanReports })));
const ReportDetail = React.lazy(() => import('./components/Reports').then(m => ({ default: m.ReportDetail })));
const ClientPortal = React.lazy(() => import('./components/ClientPortal').then(m => ({ default: m.ClientPortal })));
const ClientReportView = React.lazy(() => import('./components/ClientPortal').then(m => ({ default: m.ClientReportView })));
const HelpCenter = React.lazy(() => import('./components/Help').then(m => ({ default: m.HelpCenter })));
const Tickets = React.lazy(() => import('./components/Tickets').then(m => ({ default: m.Tickets })));
const SubscriptionPage = React.lazy(() => import('./components/Subscription').then(m => ({ default: m.SubscriptionPage })));
const PricingPlans = React.lazy(() => import('./components/Subscription').then(m => ({ default: m.PricingPlans })));
const ClientRegister = React.lazy(() => import('./components/Subscription').then(m => ({ default: m.ClientRegister })));

// Suspense fallback for lazy-loaded routes
const LazyFallback = () => <LoadingSpinner size="lg" />;

// Build version for cache busting
const BUILD_VERSION = '2026.01.18.1500';
if (typeof window !== 'undefined') {
  console.log('VulnScanner build:', BUILD_VERSION);
}

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <ColorProvider>
        <LanguageProvider>
          <AuthProvider>
            <SettingsProvider>
              <SubscriptionProvider>
              <Router>
                <ProjectProvider>
                  <ScanProvider>
                    <WebSocketProvider>
                    <AlertNotificationProvider>
                    <div className="App">
                <Suspense fallback={<LazyFallback />}>
                <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pricing" element={<PricingPlans />} />
              <Route path="/register" element={<ClientRegister />} />
              
              {/* Protected routes with layout */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects" element={
                <ProtectedRoute>
                  <Layout>
                    <Projects />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectDetail />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/overview" element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectOverview />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/scans/new" element={
                <ProtectedRoute>
                  <Layout>
                    <ScanForm />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/discovery" element={
                <ProtectedRoute>
                  <Layout>
                    <DiscoveryScan />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Project-scoped routes */}
              <Route path="/projects/:projectId/targets" element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectTargets />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/scans" element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectScans />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/scans/compare" element={
                <ProtectedRoute>
                  <Layout>
                    <ScanCompare />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/scans/:scanId" element={
                <ProtectedRoute>
                  <Layout>
                    <ScanDetail />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/schedules" element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectSchedules />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Project-scoped schedule detail and edit - accessible to clients */}
              <Route path="/projects/:projectId/schedules/:scheduleId" element={
                <ProtectedRoute>
                  <Layout>
                    <ScheduleDetail />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/schedules/:scheduleId/edit" element={
                <ProtectedRoute>
                  <Layout>
                    <ScanForm scheduleMode={true} />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/results" element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectResults />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/reports" element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectReports />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/agents" element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectAgents />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/client-reviews" element={
                <ProtectedRoute>
                  <Layout>
                    <ClientReviews />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/exposure" element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectExposure />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Project-specific findings/suppressions - available to clients */}
              <Route path="/projects/:projectId/findings" element={
                <ProtectedRoute>
                  <Layout>
                    <FindingManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Breach Monitoring - Dehashed integration */}
              <Route path="/projects/:projectId/breach-monitoring" element={
                <ProtectedRoute>
                  <Layout>
                    <BreachMonitoring />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Global client reviews route */}
              <Route path="/client-reviews" element={
                <ProtectedRoute>
                  <Layout>
                    <ClientReviews />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/client-reviews/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <ClientReviewDetail />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Routes restricted from client users */}
              <Route path="/scans" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <Scans />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/scans/compare" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <ScanCompare />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/scans/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <ScanDetail />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/scans/:id/statistics" element={
                <ProtectedRoute>
                  <Layout>
                    <ScanStatistics />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/results" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <Results />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Finding Management routes - restricted from clients */}
              <Route path="/findings" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <FindingManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/findings/suppressions" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <FindingManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/findings/tickets" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <FindingManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/exposure" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <ExposureMonitoring />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* VulnData routes - restricted from clients */}
              <Route path="/vulndata" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <VulnData />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/vulndata/rules" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <VulnDataRules />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/vulndata/databases" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <VulnDataDatabases />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/vulndata/imports" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <VulnDataImports />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/vulndata/config" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <VulnDataConfig />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/vulndata/retirejs" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <RetireJS />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/vulndata/nuclei" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <NucleiTemplateManager />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/vulndata/cve-import" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <CVEImport />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/vulndata/import-presets" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <ImportPresets />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* CISA KEV Dashboard - restricted from clients */}
              <Route path="/security/kev" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <KEVDashboard />
                  </Layout>
                </ProtectedRoute>
              } />

             {/* Security Dashboard routes - restricted from clients */}
             <Route path="/security" element={
               <ProtectedRoute denyRoles={['client']}>
                 <Layout>
                   <SecurityDashboard />
                 </Layout>
               </ProtectedRoute>
             } />

             <Route path="/security/alerts" element={
               <ProtectedRoute denyRoles={['client']}>
                 <Layout>
                   <AlertsPage />
                 </Layout>
               </ProtectedRoute>
             } />

             <Route path="/security/alerts/:id" element={
               <ProtectedRoute denyRoles={['client']}>
                 <Layout>
                   <AlertsPage />
                 </Layout>
               </ProtectedRoute>
             } />

             <Route path="/security/vulnerabilities" element={
               <ProtectedRoute denyRoles={['client']}>
                 <Layout>
                   <VulnerabilitiesPage />
                 </Layout>
               </ProtectedRoute>
             } />

             <Route path="/security/vulnerabilities/:id" element={
               <ProtectedRoute denyRoles={['client']}>
                 <Layout>
                   <VulnerabilitiesPage />
                 </Layout>
               </ProtectedRoute>
             } />

             <Route path="/security/threat-intel" element={
               <ProtectedRoute denyRoles={['client']}>
                 <Layout>
                   <ThreatIntelPage />
                 </Layout>
               </ProtectedRoute>
             } />

             <Route path="/security/threat-intel/:id" element={
               <ProtectedRoute denyRoles={['client']}>
                 <Layout>
                   <ThreatIntelPage />
                 </Layout>
               </ProtectedRoute>
             } />

            <Route path="/security/feeds" element={
              <ProtectedRoute denyRoles={['client']}>
                <Layout>
                  <FeedsPage />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Additional Security Dashboard routes - restricted from clients */}
            <Route path="/security/database-monitoring" element={
              <ProtectedRoute denyRoles={['client']}>
                <Layout>
                  <SecurityDashboard />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/security/vuln-data-management" element={
              <ProtectedRoute denyRoles={['client']}>
                <Layout>
                  <SecurityDashboard />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/security/retirejs-analysis" element={
              <ProtectedRoute denyRoles={['client']}>
                <Layout>
                  <SecurityDashboard />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/security/osint-intelligence" element={
              <ProtectedRoute denyRoles={['client']}>
                <Layout>
                  <OSINTIntelligence />
                </Layout>
              </ProtectedRoute>
            } />

           {/* WordPress Dashboard routes - restricted from clients */}
            <Route path="/wordpress" element={
              <ProtectedRoute denyRoles={['client']}>
                <Layout>
                  <WordPressDashboard />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/wordpress/vulnerabilities" element={
              <ProtectedRoute denyRoles={['client']}>
                <Layout>
                  <WordPressDashboard />
                </Layout>
              </ProtectedRoute>
            } />

             {/* Agents route - restricted from clients (top-level) */}
              <Route path="/agents" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <Agents />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Clients routes - restricted from clients (they can only see their own) */}
              <Route path="/clients" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <Clients />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Client detail - clients can access their own client page */}
              <Route path="/clients/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <ClientDetail />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Reports routes - restricted from clients (top-level) */}
              <Route path="/reports" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <ScanReports />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/reports/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <ReportDetail />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Scheduled Scans routes - restricted from clients (top-level) */}
              <Route path="/schedules" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <Schedules />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/schedules/new" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <ScanForm scheduleMode={true} />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Schedule detail/edit - clients can access if they have project permission (backend validates) */}
              <Route path="/schedules/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <ScheduleDetail />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/schedules/:id/edit" element={
                <ProtectedRoute>
                  <Layout>
                    <ScanForm scheduleMode={true} />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/schedules/new" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <ScanForm scheduleMode={true} />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Settings route - restricted from clients */}
              <Route path="/settings" element={
                <ProtectedRoute denyRoles={['client']}>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Passive Reconnaissance routes */}
              <Route path="/recon" element={
                <ProtectedRoute>
                  <Layout>
                    <ReconScan />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/recon/project/:projectId" element={
                <ProtectedRoute>
                  <Layout>
                    <ReconScan />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/recon/scans/:scanId" element={
                <ProtectedRoute>
                  <Layout>
                    <ReconResults />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/recon/attack-surface/:projectId" element={
                <ProtectedRoute>
                  <Layout>
                    <AttackSurfaceDashboard />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/recon/correlated/:projectId" element={
                <ProtectedRoute>
                  <Layout>
                    <CorrelatedFindings />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Admin route - restricted to superusers */}
              <Route path="/admin" element={
                <ProtectedRoute requireSuperuser>
                  <Layout>
                    <Admin />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Admin sub-routes for specific management UIs */}
              <Route path="/admin/health" element={
                <ProtectedRoute requireSuperuser>
                  <Layout>
                    <SystemHealth />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/discovery-patterns" element={
                <ProtectedRoute requireSuperuser>
                  <Layout>
                    <DiscoveryPatterns />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/recon-policies" element={
                <ProtectedRoute requireSuperuser>
                  <Layout>
                    <ReconPolicies />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/vuln-context" element={
                <ProtectedRoute requireSuperuser>
                  <Layout>
                    <VulnContextEditor />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/engine-limits" element={
                <ProtectedRoute requireSuperuser>
                  <Layout>
                    <EngineLimits />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Subscription management - accessible to all authenticated users */}
              <Route path="/subscription" element={
                <ProtectedRoute>
                  <Layout>
                    <SubscriptionPage />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Client Portal routes - for client users */}
              <Route path="/portal" element={
                <ProtectedRoute>
                  <Layout>
                    <ClientPortal />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/portal/reports/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <ClientReportView />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Tickets routes - accessible to all authenticated users */}
              <Route path="/tickets" element={
                <ProtectedRoute>
                  <Layout>
                    <Tickets />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/tickets/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <Tickets />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Help Center route */}
              <Route path="/help" element={
                <ProtectedRoute>
                  <Layout>
                    <HelpCenter />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Client Account page - for client users to manage their account */}
              <Route path="/account" element={
                <ProtectedRoute allowedRoles={['client', 'client_admin']}>
                  <Layout>
                    <ClientAccount />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Redirect unknown routes to dashboard */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
                </Suspense>
                </div>
                </AlertNotificationProvider>
                </WebSocketProvider>
                </ScanProvider>
              </ProjectProvider>
            </Router>
              </SubscriptionProvider>
          </SettingsProvider>
          </AuthProvider>
        </LanguageProvider>
      </ColorProvider>
    </ThemeProvider>
  );
};

export default App;